import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bankReceipts, type BankReceiptListItem } from '@/api/bankDocs';
import { contractors as contractorsApi } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BankReceiptsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['bank-receipts'],
    queryFn: () => bankReceipts.list({ limit: 200 }),
  });
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColumnDef<BankReceiptListItem>[]>(() => [
    { key: 'document_number', header: t('bd.number'), width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: t('bd.date'), width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString(i18n.language) : '',
    },
    {
      key: 'contractor_id', header: t('br.payer'),
      sortValue: (r) => (r.contractor_id ? cName.get(r.contractor_id) ?? '' : ''),
      cell: (r) => (r.contractor_id ? (cName.get(r.contractor_id) ?? '') : ''),
    },
    {
      key: 'payment_purpose', header: t('bd.purpose'),
      sortValue: (r) => r.payment_purpose ?? '',
      cell: (r) => r.payment_purpose ?? '',
    },
    {
      key: 'amount', header: t('bd.amount'), width: 140, align: 'right',
      sortValue: (r) => Number(r.amount),
      cell: (r) => fmt(r.amount),
    },
    {
      key: 'posted', header: t('bd.posted'), width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no'),
    },
  ], [t, i18n.language, cName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('br.title')}</h2>
        <button className="primary" onClick={() => navigate('/bank-receipts/new')}>
          + {t('br.new')}
        </button>
      </div>
      <DataTable<BankReceiptListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/bank-receipts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
